// src/partner/PartnerDashboard.js
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Alert,
  Container,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import DreamyAppsLogo from "../components/DreamyAppsLogo";

const SIGNUP_BASE = "https://imaginativessolutions.com/records/signup";

function PartnerDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [referralCode, setReferralCode] = useState("");
  const [copied, setCopied] = useState("");

  useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const decoded = jwtDecode(token);
      setReferralCode(decoded.referralCode || "");
    } catch (e) {
      console.error("PartnerDashboard token error:", e);
    }
  }, []);

  const normalLink = useMemo(
    () =>
      referralCode
        ? `${SIGNUP_BASE}?ref=${encodeURIComponent(referralCode)}`
        : "",
    [referralCode]
  );

  const networkerLink = useMemo(
    () =>
      referralCode
        ? `${SIGNUP_BASE}?ref=${encodeURIComponent(referralCode)}&role=networker`
        : "",
    [referralCode]
  );

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(""), 2000);
    } catch (e) {
      console.error("Copy failed:", e);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar
        position="static"
        sx={{
          background: "linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <DreamyAppsLogo size="medium" showText variant="minimal" />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button color="inherit" onClick={() => navigate("/user")}>
              User dashboard
            </Button>
            <IconButton color="inherit" onClick={onLogout} aria-label="Logout">
              <LogoutIcon />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
          Partner portal
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Use the networker link so new networkers are placed directly under you
          with their own tree. The normal link still uses binary placement for
          regular members.
        </Typography>

        {copied ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {copied} copied
          </Alert>
        ) : null}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Networker invite link
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Signups from this link become networkers under you (extra 5% on their
            tree from company holdback).
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              fullWidth
              size="small"
              value={networkerLink}
              InputProps={{ readOnly: true }}
            />
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              disabled={!networkerLink}
              onClick={() => copy(networkerLink, "Networker link")}
            >
              Copy
            </Button>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Normal member invite link
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Regular referrals — binary placement as today.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              fullWidth
              size="small"
              value={normalLink}
              InputProps={{ readOnly: true }}
            />
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              disabled={!normalLink}
              onClick={() => copy(normalLink, "Normal link")}
            >
              Copy
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default PartnerDashboard;
